import { SocketClient } from "cbcore-ts"
import { ProcurementTemplateLockButton, type ProcurementTemplateLockControlConfiguration } from "./ProcurementTemplateLockButton"
import { IF, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { CompanyViewController } from "../CompanyView/CompanyViewController"
import { RHCore } from "../Core/RHCore"
import {
    CBDataView,
    CBDataViewCellDescriptorWithObject,
    CBSelectionColumnDescriptor
} from "../Custom components/CB/CBDataView"
import { RHFocusManager } from "../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHNativeFocusItem } from "../Custom components/RH/Keyboard navigation/RHNativeFocusItem"
import { RHAlertBannerView } from "../Custom components/RH/RHAlertBannerView"
import { RHCalloutView } from "../Custom components/RH/RHCalloutView"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHTeamMemberDisplayData, RHTeamUserReferenceResolver } from "../Custom components/RH/RHTeamRoleSectionView"
import { formatRHDateTimeWithSecondsValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHColors, RHFont, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import type { AuditLogEntry } from "../SocketClientFunctions"
import { RHCompanyMemberPresentation } from "../CompanyView/CompanyAdministration/CompanyMemberPresentation"
import type { RHReferenceLike } from "../Core/RHReference"


export type RHTeamRoleMatrixRole = "responsible" | "authorized" | "observer" | "evaluator"
export type RHTeamSelfAdditionRole = Exclude<RHTeamRoleMatrixRole, "responsible">
type ProcurementTeamEditableRole = RHTeamRoleMatrixRole
type ProcurementTeamUsersLoadState = "loading" | "loaded" | "failed"

export interface RHTeamRoleMatrixPresentationConfiguration {
    visibleRoles: RHTeamRoleMatrixRole[]
    editableRoles: RHTeamRoleMatrixRole[]
    roleColumnWidths?: Partial<Record<RHTeamRoleMatrixRole, number>>
    roleTitles?: Partial<Record<RHTeamRoleMatrixRole, string>>
    roleDescriptions?: Partial<Record<RHTeamRoleMatrixRole, string>>
    canCurrentUserChangeOwnRole?: boolean
    nonEditableUserIds?: string[]
    shouldShowHeaderContent?: boolean
    shouldShowCompanyMemberInvitationNotice?: boolean
}

export interface RHTeamRoleMatrixAssignments {
    responsibleUser?: RHReferenceLike
    authorizedUsers?: RHReferenceLike[]
    observerUsers?: RHReferenceLike[]
    evaluatorUsers?: RHReferenceLike[]
}

const BIDDER_TEAM_CONFLICT_MESSAGE =
    "Võimalik huvide konflikt: sama kasutajakonto osaleb sellel hankel ka pakkuja poolel."
const DEFAULT_COMPANY_MEMBER_INVITATION_TARGET =
    "/#settings[language:est,mode:procurer]account[tab:company]" +
    `company[tab:administration,action:inviteMember,${
        CompanyViewController.ParameterIdentifierName.sourceNewProcurement
    }:1]`

type ProcurementTeamEmployeeRowData = {
    userId: string
    name: string
    nameHTML: string
    email: string
    emailHTML: string
    employeeSearchText: string
    companyName: string
    companyNameTooltip: string
    responsibleSelected: boolean
    authorizedSelected: boolean
    observerSelected: boolean
    evaluatorSelected: boolean
    conflictOfInterestStatusText: string
    hasBidderSideParticipation: boolean
}

type ProcurementTeamHistoryRowData = {
    identifier: string
    timestamp: string
    timestampText: string
    actor: string
    action: string
    member: string
    role: string
}


// The fields of a team-change audit entry's eventData that the history list reads.
interface RHTeamHistoryEventData {
    previousResponsibleUserId?: string
    responsibleUserId?: string
    previousResponsibleUserLabel?: string
    responsibleUserLabel?: string
    userId?: string
    role?: string
    roleSnapshot?: string
    userLabel?: string
    addedMembers?: RHReferenceLike[]
    removedMembers?: RHReferenceLike[]
}


export class ProcurementTeamView extends UIView {

    readonly templateLockButton = new ProcurementTemplateLockButton("Hankemeeskond")

    configureTemplateLock(configuration?: ProcurementTemplateLockControlConfiguration) {
        if (configuration) {
            this.templateLockButton.configure(configuration)
            this.templateLockButton.addedAsSubviewToView(this)
        }
        else {
            this.templateLockButton.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }

    static _companyMemberInvitationNoticeHTMLWithLinkTarget(linkTarget: string): string {
        return "Valida saab vaid sinu ettevõttega seotud liikmeid. Kõik ettevõttega seotud liikmed kuvatakse tabelis automaatselt. " +
            `<a href="${linkTarget}" ` +
            `style="color: ${RHColors.primaryBlue.stringValue}; ` +
            `text-decoration: underline; font-weight: ${RHFont.weightSemibold};">` +
            "Kutsu liige ettevõttesse.</a>"
    }
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Hankemeeskond",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this)
    
    readonly explanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Vali igale töötajale üks roll. Vastutav haldab hanget ja lõplikke toiminguid. Volitatud isik saab teha igapäevaseid hanketoiminguid. Vaatleja saab hankega tutvuda. Hindaja osaleb pakkumuste hindamises. Enda olemasolevat rolli ei saa siin vähendada.",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)

    readonly companyMemberInvitationCalloutView = new RHCalloutView("info", "", "")
        .configuredWithObject({
            style: { boxSizing: "border-box" },
            _titleLabel: {
                innerHTML: ProcurementTeamView._companyMemberInvitationNoticeHTMLWithLinkTarget(
                    DEFAULT_COMPANY_MEMBER_INVITATION_TARGET
                ),
                style: {
                    fontSize: RHFont.sizeSmall,
                    fontWeight: RHFont.weightRegular,
                    overflowWrap: "anywhere",
                    whiteSpace: "normal"
                }
            }
        })
        .addedAsSubviewToView(this)
    
    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Error
        })

    readonly emptyStateBanner = new RHAlertBannerView()
        .configuredWithObject({
            text: "Ettevõttes ei ole aktiivseid kasutajaid, keda meeskonda lisada.",
            alertType: RHAlertBannerView.type.Warning,
            textPresentation: RHAlertBannerView.textPresentation.Outcome
        })

    readonly currentUserSelfAdditionCalloutView = new RHCalloutView(
        "info",
        "Peakasutajana on Sul võimalik lisada ennast meeskonda.",
        ""
    )
    
    readonly usersDataView = new RHDataView<ProcurementTeamEmployeeRowData>()
        .configuredWithObject({
            noDataMessage: "Kasutajaid laaditakse...",
            hasResizableColumns: NO,
            pageScrollForwardingMode: "pageFirst",
            tableView: {
                allRowsHaveEqualHeight: NO
            },
            maximumIntrinsicContentHeight: 520
        })
        .addedAsSubviewToView(this)

    readonly historyTitleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Meeskonna ajalugu",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })

    readonly historyExplanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Otsi, sorteeri ja vaata, kes meeskonda lisati või sealt eemaldati ning kes kinnitas huvide konflikti puudumist.",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })

    readonly historyDataView = new RHDataView<ProcurementTeamHistoryRowData>()
        .configuredWithObject({
            noDataMessage: "Meeskonna ajalugu puudub.",
            hasResizableColumns: NO,
            pageScrollForwardingMode: "pageFirst",
            tableView: {
                allRowsHaveEqualHeight: YES
            }
        })
    
    teamDidChange?: () => void
    currentUserSelfAdditionWasRequested?: (role: RHTeamSelfAdditionRole) => void
    
    _allUsers: RHTeamMemberDisplayData[] = []
    readonly _auditorUserIds = new Set<string>()
    readonly _rowDataByUserId = new Map<string, ProcurementTeamEmployeeRowData>()
    readonly _companyNamePresentationByUserId = new Map<string, {
        text: string
        tooltip: string
    }>()
    _responsibleUserId = ""
    _authorizedUserIds: string[] = []
    _observerUserIds: string[] = []
    _evaluatorUserIds: string[] = []
    _conflictOfInterestConfirmations: Array<{
        user: RHReferenceLike
        roleSnapshot: string
        confirmedAt: Date
    }> = []
    readonly _bidderSideParticipantUserIds = new Set<string>()
    _historyEntries: AuditLogEntry[] = []
    _usersLoadState: ProcurementTeamUsersLoadState = "loading"
    _companyUsersLoadGeneration = 0
    addsCurrentUserWhenMissing = YES
    canEditResponsibleRole = YES
    canEditMemberRoles = YES
    showsHistory = NO
    _showsConflictConfirmationStatus = NO
    _visibleRoles = new Set<RHTeamRoleMatrixRole>([
        "responsible",
        "authorized",
        "observer",
        "evaluator"
    ])
    _editableRoles?: Set<RHTeamRoleMatrixRole>
    _roleColumnWidths: Partial<Record<RHTeamRoleMatrixRole, number>> = {}
    _roleTitles: Partial<Record<RHTeamRoleMatrixRole, string>> = {}
    _roleDescriptions: Partial<Record<RHTeamRoleMatrixRole, string>> = {}
    _canCurrentUserChangeOwnRole = NO
    _nonEditableUserIds = new Set<string>()
    _canCurrentUserAddSelfToTeam = NO
    _shouldShowHeaderContent = YES
    _shouldShowCompanyMemberInvitationNotice = YES

    get showsConflictConfirmationStatus(): boolean {
        return this._showsConflictConfirmationStatus
    }

    set showsConflictConfirmationStatus(show: boolean) {
        if (this._showsConflictConfirmationStatus === show) {
            return
        }
        this._showsConflictConfirmationStatus = show
        this._configureUsersDataView()
        this._reloadUsersDataView()
        this.setNeedsLayoutUpToRootView()
    }

    get canCurrentUserAddSelfToTeam(): boolean {
        return this._canCurrentUserAddSelfToTeam
    }

    set canCurrentUserAddSelfToTeam(canAdd: boolean) {
        if (this._canCurrentUserAddSelfToTeam === canAdd) {
            return
        }
        this._canCurrentUserAddSelfToTeam = canAdd
        this._updateCurrentUserSelfAdditionPresentation()
        this._reloadUsersDataView()
    }
    
    constructor(elementID?: string) {
        super(elementID)
        this._configureUsersDataView()
        this._configureHistoryDataView()
        this._reloadUsersDataView()
    }


    set companyMemberInvitationTarget(target: string) {
        this.companyMemberInvitationCalloutView._titleLabel.innerHTML =
            ProcurementTeamView._companyMemberInvitationNoticeHTMLWithLinkTarget(target)
        this.companyMemberInvitationCalloutView.setNeedsLayoutUpToRootView()
    }

    configureRoleMatrixPresentation(configuration: RHTeamRoleMatrixPresentationConfiguration) {
        this._visibleRoles = new Set(configuration.visibleRoles)
        this._editableRoles = new Set(configuration.editableRoles)
        this._roleColumnWidths = configuration.roleColumnWidths ?? {}
        this._roleTitles = configuration.roleTitles ?? {}
        this._roleDescriptions = configuration.roleDescriptions ?? {}
        this._canCurrentUserChangeOwnRole = configuration.canCurrentUserChangeOwnRole ?? NO
        this._nonEditableUserIds = new Set(configuration.nonEditableUserIds)
        this._shouldShowHeaderContent = configuration.shouldShowHeaderContent ?? YES
        this._shouldShowCompanyMemberInvitationNotice =
            configuration.shouldShowCompanyMemberInvitationNotice ?? YES
        this._applyRoleMatrixPresentation()
        this._configureUsersDataView()
        this._reloadUsersDataView()
    }

    configureRoleAssignments(assignments: RHTeamRoleMatrixAssignments) {
        this._responsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
            assignments.responsibleUser
        )
        this._authorizedUserIds = this._userIdsFromReferences(assignments.authorizedUsers)
        this._observerUserIds = this._userIdsFromReferences(assignments.observerUsers)
        this._evaluatorUserIds = this._userIdsFromReferences(assignments.evaluatorUsers)
        this._deduplicateAcrossRoles()
        this._ensureDefaultResponsibleUser()
        this._ensureCurrentUserHasRole()
        this._ensureKnownUsersForSelections()
        this._updateCurrentUserSelfAdditionPresentation()
        this._reloadUsersDataView()
    }

    configureHistoryEntries(historyEntries?: AuditLogEntry[]) {
        this._historyEntries = historyEntries ?? []
        this.showsHistory = NO
        if (historyEntries !== undefined) {
            this.showsHistory = YES
        }
        this._reloadHistoryDataView()
        this.setNeedsLayoutUpToRootView()
    }

    _applyRoleMatrixPresentation() {
        if (this._shouldShowHeaderContent === YES) {
            this.titleLabel.addedAsSubviewToView(this)
            this.explanationLabel.addedAsSubviewToView(this)
        }
        else {
            this.titleLabel.removeFromSuperview()
            this.explanationLabel.removeFromSuperview()
        }
        if (this._shouldShowCompanyMemberInvitationNotice === YES) {
            this.companyMemberInvitationCalloutView.addedAsSubviewToView(this)
        }
        else {
            this.companyMemberInvitationCalloutView.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    get responsibleUserId(): string {
        return this._responsibleUserId
    }
    
    get authorizedUserIds(): string[] {
        return [...this._authorizedUserIds]
    }
    
    get observerUserIds(): string[] {
        return [...this._observerUserIds]
    }
    
    get evaluatorUserIds(): string[] {
        return [...this._evaluatorUserIds]
    }
    
    async loadCompanyUsers(companyIdForUsers?: string) {
        const loadGeneration = ++this._companyUsersLoadGeneration
        this._usersLoadState = "loading"
        this._allUsers = []
        this._auditorUserIds.clear()
        this._companyNamePresentationByUserId.clear()
        this._reloadUsersDataView()

        try {
            if (!companyIdForUsers) {
                await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
                if (loadGeneration !== this._companyUsersLoadGeneration) {
                    return
                }
                companyIdForUsers = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
            }
            if (!companyIdForUsers) {
                this._applyCompanyUsersLoadFailure()
                return
            }
            const response = (await SocketClient.ListCompanyMembersAndPending({
                companyId: companyIdForUsers
            })).result
            if (loadGeneration !== this._companyUsersLoadGeneration) {
                return
            }
            if (!response?.success || !Array.isArray(response.members)) {
                this._applyCompanyUsersLoadFailure()
                return
            }

            const usersById = new Map<string, RHTeamMemberDisplayData>()
            for (const user of response.members) {
                if (user.status === "active") {
                    const member = RHTeamUserReferenceResolver.memberFromUserData(user)
                    this._companyNamePresentationByUserId.set(
                        member.userId,
                        RHCompanyMemberPresentation.companyNamesPresentationForMember({
                            companies: user.companies.filter(
                                company => company._id === companyIdForUsers
                            )
                        })
                    )
                    if (user.roles.includes("auditor")) {
                        this._auditorUserIds.add(member.userId)
                        continue
                    }
                    usersById.set(member.userId, member)
                }
            }
            this._allUsers = [...usersById.values()]

            this._usersLoadState = "loaded"
            this._ensureDefaultResponsibleUser()
            this._ensureCurrentUserHasRole()
            this._ensureKnownUsersForSelections()
            this._reloadUsersDataView()
        }
        catch (error) {
            if (loadGeneration !== this._companyUsersLoadGeneration) {
                return
            }
            console.error("Failed to load company users", error)
            this._applyCompanyUsersLoadFailure()
        }
    }

    _applyCompanyUsersLoadFailure() {
        this._usersLoadState = "failed"
        this._allUsers = []
        this._auditorUserIds.clear()
        this._companyNamePresentationByUserId.clear()
        this.errorBanner.text = "Ettevõtte kasutajate laadimine ebaõnnestus."
        // The current user remains a valid safe default even when the wider
        // company directory is unavailable. Keep the team invariant intact so
        // a transient directory failure cannot leave the draft without an owner.
        this._ensureDefaultResponsibleUser()
        this._ensureCurrentUserHasRole()
        this._ensureKnownUsersForSelections()
        this._reloadUsersDataView()
    }
    
    configureFromProcurement(procurement: {
        responsibleProcuringUser?: RHReferenceLike
        primaryProcuringUser?: RHReferenceLike
        authorizedProcuringUsers?: RHReferenceLike[]
        secondaryProcuringUsers?: RHReferenceLike[]
        observerProcuringUsers?: RHReferenceLike[]
        evaluatorProcuringUsers?: RHReferenceLike[]
        userIdsWithActiveOfferConflicts?: string[]
        conflictOfInterestConfirmations?: Array<{
            user: RHReferenceLike
            roleSnapshot: string
            confirmedAt: Date
        }>
        history?: AuditLogEntry[]
    }) {
        this._responsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
            procurement.responsibleProcuringUser
        )
        if (this._responsibleUserId.length === 0) {
            this._responsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
                procurement.primaryProcuringUser
            )
        }
        
        this._authorizedUserIds = this._userIdsFromReferences(procurement.authorizedProcuringUsers)
        if (this._authorizedUserIds.length === 0) {
            this._authorizedUserIds = this._userIdsFromReferences(procurement.secondaryProcuringUsers)
        }
        this._observerUserIds = this._userIdsFromReferences(procurement.observerProcuringUsers)
        this._evaluatorUserIds = this._userIdsFromReferences(procurement.evaluatorProcuringUsers)
        this._bidderSideParticipantUserIds.clear()
        for (const userId of procurement.userIdsWithActiveOfferConflicts ?? []) {
            this._bidderSideParticipantUserIds.add(userId)
        }
        this._conflictOfInterestConfirmations = procurement.conflictOfInterestConfirmations ?? []
        this._deduplicateAcrossRoles()
        this._ensureDefaultResponsibleUser()
        this._ensureCurrentUserHasRole()
        this._ensureKnownUsersForSelections()
        this._updateCurrentUserSelfAdditionPresentation()
        this._reloadUsersDataView()
        this.configureHistoryEntries(procurement.history)
    }
    
    resetToCurrentUser() {
        this._responsibleUserId = ""
        this._authorizedUserIds = []
        this._observerUserIds = []
        this._evaluatorUserIds = []
        this._bidderSideParticipantUserIds.clear()
        this._ensureDefaultResponsibleUser()
        this._ensureCurrentUserHasRole()
        this._ensureKnownUsersForSelections()
        this._reloadUsersDataView()
    }
    
    validationMessage(): string | undefined {
        // Session data can become available after the team view's initial load.
        // Re-establish the invariant at the final validation boundary instead of
        // trusting an earlier asynchronous initialization attempt.
        if (this._responsibleUserId.length === 0) {
            this._ensureDefaultResponsibleUser()
            this._ensureCurrentUserHasRole()
            this._ensureKnownUsersForSelections()
            this._reloadUsersDataView()
        }
        if (this._responsibleUserId.length === 0) {
            return "Hankele tuleb määrata vastutav isik."
        }
        return undefined
    }
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHNativeFocusItem)[] = []
        if (this.companyMemberInvitationCalloutView.superview === this) {
            const companyMemberInvitationLink =
                this.companyMemberInvitationCalloutView.viewHTMLElement.querySelector("a")
            if (companyMemberInvitationLink instanceof HTMLElement) {
                items.push(new RHNativeFocusItem(companyMemberInvitationLink))
            }
        }
        if (this.usersDataView.superview === this) {
            items.push(this.usersDataView.searchTextField)
            items.push(this.usersDataView._gridContainerView)
        }
        if (this.historyDataView.superview === this) {
            items.push(this.historyDataView.searchTextField)
            items.push(this.historyDataView._gridContainerView)
        }
        return items
    })
    
    _configureUsersDataView() {
        this.usersDataView.searchTextField.placeholderText = "Filtreeri töötajaid selles tabelis"
        this.usersDataView.didUpdateTableAfterSearchTextChange = () => {
            this._reloadUsersDataView()
        }
        this.usersDataView.selectionDidChange = selectionIdentifier => {
            this._selectionDidChange(selectionIdentifier)
        }
        const roleDescriptors = [
            this._selectionDescriptorForRoleAndKeyPath("responsible", "responsibleSelected", "single"),
            this._selectionDescriptorForRoleAndKeyPath("authorized",  "authorizedSelected",  "multiple"),
            this._selectionDescriptorForRoleAndKeyPath("observer",    "observerSelected",    "multiple"),
            this._selectionDescriptorForRoleAndKeyPath("evaluator",   "evaluatorSelected",   "multiple")
        ]
        const roleOrder: ProcurementTeamEditableRole[] = [
            "responsible",
            "authorized",
            "observer",
            "evaluator"
        ]
        const visibleRoleDescriptors = roleDescriptors.filter((_descriptor, index) => {
            return this._visibleRoles.has(roleOrder[index])
        })
        this.usersDataView.descriptors = [
            CBDataViewCellDescriptorWithObject<ProcurementTeamEmployeeRowData>({
                title: "Nimi",
                keyPath: "employeeSearchText",
                allowSorting: YES,
                weight: 3,
                alignment: UITextView.textAlignment.left,
                sortingInstructions: ownState => {
                    if (ownState === "unordered") {
                        return []
                    }
                    return [
                        { keyPath: "name", dataType: "string", direction: ownState },
                        { keyPath: "email", dataType: "string", direction: "ascending" }
                    ]
                },
                tooltipText: dataItem => {
                    return [dataItem.name, dataItem.email]
                        .filter(value => value.length > 0)
                        .join("\n")
                },
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.nameHTML
                    cellView.configureAsClampedMultilineText(2)
                }
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamEmployeeRowData>({
                title: "Ettevõte",
                keyPath: "companyName",
                allowSorting: YES,
                weight: 3.6,
                alignment: UITextView.textAlignment.left,
                tooltipText: dataItem => dataItem.companyNameTooltip,
                initCell: cellView => {
                    cellView.configureAsMultilineTextPreservingLineBreaks()
                },
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.companyName
                }
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamEmployeeRowData>({
                title: "E-post",
                keyPath: "email",
                allowSorting: YES,
                weight: 3.4,
                alignment: UITextView.textAlignment.left,
                tooltipText: dataItem => dataItem.email,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.emailHTML
                    cellView.configureAsClampedMultilineText(2)
                }
            }),
            ...visibleRoleDescriptors,
            CBDataViewCellDescriptorWithObject<ProcurementTeamEmployeeRowData>({
                title: "Huvide konflikti kinnitus",
                keyPath: "conflictOfInterestStatusText",
                allowSorting: YES,
                hidden: this.showsConflictConfirmationStatus !== YES,
                absoluteWidth: 176,
                alignment: UITextView.textAlignment.left,
                tooltipText: dataItem => dataItem.conflictOfInterestStatusText,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.conflictOfInterestStatusText
                    cellView.configureAsClampedMultilineText(2)
                    cellView.titleLabel!.textColor = RHColors.textPrimary
                    if (dataItem.hasBidderSideParticipation && this._roleForUserId(dataItem.userId)) {
                        cellView.titleLabel!.textColor = RHColors.dangerBadgeText
                    }
                }
            })
        ]
    }

    _configureHistoryDataView() {
        this.historyDataView.searchTextField.placeholderText = "Otsi meeskonna ajaloost"
        this.historyDataView.descriptors = [
            CBDataViewCellDescriptorWithObject<ProcurementTeamHistoryRowData>({
                title: "Aeg",
                keyPath: "timestamp",
                allowSorting: YES,
                dataType: "string",
                initialOrderingState: "descending",
                weight: 1.5,
                alignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem) => {
                    cellView.text = dataItem.timestampText
                }
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamHistoryRowData>({
                title: "Tegija",
                keyPath: "actor",
                allowSorting: YES,
                weight: 1.6,
                alignment: UITextView.textAlignment.left
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamHistoryRowData>({
                title: "Tegevus",
                keyPath: "action",
                allowSorting: YES,
                weight: 1.8,
                alignment: UITextView.textAlignment.left
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamHistoryRowData>({
                title: "Meeskonnaliige",
                keyPath: "member",
                allowSorting: YES,
                weight: 1.8,
                alignment: UITextView.textAlignment.left
            }),
            CBDataViewCellDescriptorWithObject<ProcurementTeamHistoryRowData>({
                title: "Roll",
                keyPath: "role",
                allowSorting: YES,
                weight: 1.2,
                alignment: UITextView.textAlignment.left
            })
        ]
    }
    
    _selectionDescriptorForRoleAndKeyPath(
        role: ProcurementTeamEditableRole,
        keyPath: keyof ProcurementTeamEmployeeRowData & string,
        selectionMode: "single" | "multiple"
    ): CBSelectionColumnDescriptor<ProcurementTeamEmployeeRowData, RHCheckbox> {
        const identifier = this._selectionIdentifierForRole(role)
        const title = this._roleTitle(role)
        const hoverText = this._roleDescription(role)
        return {
            type: "selection",
            identifier,
            keyPath,
            title,
            selectionMode,
            absoluteWidth: this._roleColumnWidths[role] ?? 104,
            enabled: dataItem => this._isSelectionEnabledForRowDataAndRole(dataItem, role),
            masterSelectionEnabled: NO,
            initCustomView: () => {
                const checkbox = new RHCheckbox()
                    .configuredWithObject({
                        titleLabel: { text: "" },
                        hoverText,
                        centersControlHorizontally: YES
                    }) as RHCheckbox
                checkbox.checkbox.viewHTMLElement.setAttribute("aria-label", title)
                return checkbox
            },
            initCell: cellView => {
                cellView.contentInsets.left = 0
                cellView.contentInsets.right = 0
                cellView.contentInsets.top = 0
                cellView.contentInsets.bottom = 0
            },
            updateCell: (cellView, dataItem) => {
                let checkboxHoverText = hoverText
                if (dataItem.hasBidderSideParticipation) {
                    checkboxHoverText += "\n" + dataItem.conflictOfInterestStatusText
                }
                else if (!this._isSelectionEnabledForRowDataAndRole(dataItem, role)) {
                    checkboxHoverText += "\nSeda rolli ei saa muuta või see on sinu enda olemasolev roll."
                }
                cellView.hoverText = checkboxHoverText
                cellView.customView!.hoverText = checkboxHoverText
                cellView.customView!.titleLabel.text = ""
            }
        }
    }

    _roleTitle(role: ProcurementTeamEditableRole): string {
        const configuredTitle = this._roleTitles[role]
        if (configuredTitle) {
            return configuredTitle
        }
        if (role === "responsible") { return "Vastutav" }
        if (role === "authorized") { return "Volitatud" }
        if (role === "observer") { return "Vaatleja" }
        return "Hindaja"
    }

    _roleDescription(role: ProcurementTeamEditableRole): string {
        const configuredDescription = this._roleDescriptions[role]
        if (configuredDescription) {
            return configuredDescription
        }
        if (role === "responsible") {
            return "Vastutav isik haldab hanget, meeskonda ja lõplikke toiminguid."
        }
        if (role === "authorized") {
            return "Volitatud kasutaja saab teha hanke igapäevaseid toiminguid."
        }
        if (role === "observer") {
            return "Vaatleja saab hankega tutvuda, kuid ei muuda hanke andmeid."
        }
        return "Hindaja osaleb pakkumuste hindamisel."
    }

    _isSelectionEnabledForRowDataAndRole(
        rowData: ProcurementTeamEmployeeRowData,
        role: ProcurementTeamEditableRole
    ): boolean {
        if (this._usersLoadState !== "loaded") {
            return NO
        }
        if (this._auditorUserIds.has(rowData.userId)) {
            return NO
        }
        if (this._nonEditableUserIds.has(rowData.userId)) {
            return NO
        }
        const currentUserId = this._currentUserId()
        if (
            this._canCurrentUserRequestTeamSelfAddition() &&
            rowData.userId === currentUserId
        ) {
            return role !== "responsible"
        }
        if (this._editableRoles) {
            if (!this._editableRoles.has(role)) {
                return NO
            }
        }
        else {
            if (role === "responsible" && this.canEditResponsibleRole !== YES) {
                return NO
            }
            if (role !== "responsible" && this.canEditMemberRoles !== YES) {
                return NO
            }
        }
        if (currentUserId.length === 0) {
            return YES
        }
        if (this._canCurrentUserChangeOwnRole !== YES) {
            const currentUserRole = this._roleForUserId(currentUserId)
            if (currentUserRole === role) {
                return NO
            }
            if (rowData.userId === currentUserId) {
                return NO
            }
        }
        return YES
    }

    _canCurrentUserRequestTeamSelfAddition(): boolean {
        if (this._canCurrentUserAddSelfToTeam !== YES) {
            return NO
        }
        const currentUserId = this._currentUserId()
        if (currentUserId.length === 0) {
            return NO
        }
        const currentUserRole = this._roleForUserId(currentUserId)
        if (!currentUserRole) {
            return YES
        }
        return !this._visibleRoles.has(currentUserRole)
    }

    _updateCurrentUserSelfAdditionPresentation() {
        if (this._canCurrentUserRequestTeamSelfAddition()) {
            this.currentUserSelfAdditionCalloutView.addedAsSubviewToView(this)
        }
        else {
            this.currentUserSelfAdditionCalloutView.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    _selectionIdentifierForRole(role: ProcurementTeamEditableRole): string {
        return this.elementID + "ProcurementTeam" + role
    }

    _currentUserId(): string {
        return RHTeamUserReferenceResolver.userIdFromReference(
            RHCore.sharedInstance.userProfile
        )
    }
    
    _userIdsFromReferences(references: RHReferenceLike[] | undefined): string[] {
        const userIds: string[] = []
        for (const reference of references ?? []) {
            const userId = RHTeamUserReferenceResolver.userIdFromReference(reference)
            if (userId.length > 0 && !userIds.contains(userId)) {
                userIds.push(userId)
            }
        }
        return userIds
    }
    
    _ensureDefaultResponsibleUser() {
        if (this._responsibleUserId.length > 0) {
            return
        }
        if (this.addsCurrentUserWhenMissing !== YES) {
            return
        }
        const currentUserId = this._currentUserId()
        if (currentUserId.length > 0) {
            this._responsibleUserId = currentUserId
        }
    }

    _ensureCurrentUserHasRole() {
        const currentUserId = this._currentUserId()
        if (currentUserId.length === 0) {
            return
        }
        if (this._roleForUserId(currentUserId)) {
            return
        }
        if (this.addsCurrentUserWhenMissing !== YES) {
            return
        }
        if (this._responsibleUserId.length === 0) {
            this._responsibleUserId = currentUserId
            return
        }
        if (!this._authorizedUserIds.contains(currentUserId)) {
            this._authorizedUserIds.push(currentUserId)
        }
    }
    
    _ensureKnownUsersForSelections() {
        const teamUserIds = this._teamUserIds()
        for (const userId of teamUserIds) {
            this._ensureKnownUser(userId)
        }
    }
    
    _ensureKnownUser(userId: string) {
        if (this._allUsers.some(user => user.userId === userId)) {
            return
        }
        
        const userProfile = RHCore.sharedInstance.userProfile
        if (userProfile && userProfile._id === userId) {
            this._allUsers.push(RHTeamUserReferenceResolver.memberFromReference(userProfile))
            return
        }
        
        this._allUsers.push({
            userId,
            name: userId,
            email: ""
        })
    }
    
    _deduplicateAcrossRoles() {
        this._authorizedUserIds = this._uniqueUserIds(this._authorizedUserIds)
        this._observerUserIds = this._uniqueUserIds(this._observerUserIds)
        this._evaluatorUserIds = this._uniqueUserIds(this._evaluatorUserIds)
        
        if (this._responsibleUserId.length > 0) {
            this._authorizedUserIds = this._authorizedUserIds.filter(userId => userId !== this._responsibleUserId)
            this._observerUserIds = this._observerUserIds.filter(userId => userId !== this._responsibleUserId)
            this._evaluatorUserIds = this._evaluatorUserIds.filter(userId => userId !== this._responsibleUserId)
        }
        this._observerUserIds = this._observerUserIds.filter(userId => !this._authorizedUserIds.contains(userId))
        this._evaluatorUserIds = this._evaluatorUserIds.filter(userId =>
            !this._authorizedUserIds.contains(userId) && !this._observerUserIds.contains(userId)
        )
    }
    
    _uniqueUserIds(userIds: string[]): string[] {
        const uniqueUserIds: string[] = []
        for (const userId of userIds) {
            if (userId.length > 0 && !uniqueUserIds.contains(userId)) {
                uniqueUserIds.push(userId)
            }
        }
        return uniqueUserIds
    }
    
    _teamUserIds(): string[] {
        const teamUserIds: string[] = []
        if (this._responsibleUserId.length > 0) {
            teamUserIds.push(this._responsibleUserId)
        }
        teamUserIds.push(...this._authorizedUserIds)
        teamUserIds.push(...this._observerUserIds)
        teamUserIds.push(...this._evaluatorUserIds)
        return this._uniqueUserIds(teamUserIds)
    }
    
    _roleForUserId(userId: string): ProcurementTeamEditableRole | undefined {
        if (this._responsibleUserId === userId) {
            return "responsible"
        }
        if (this._authorizedUserIds.contains(userId)) {
            return "authorized"
        }
        if (this._observerUserIds.contains(userId)) {
            return "observer"
        }
        if (this._evaluatorUserIds.contains(userId)) {
            return "evaluator"
        }
        return undefined
    }
    
    _selectionDidChange(selectionIdentifier: string) {
        const role = (["responsible", "authorized", "observer", "evaluator"] as ProcurementTeamEditableRole[])
            .find(candidateRole => this._selectionIdentifierForRole(candidateRole) === selectionIdentifier)
        if (!role) {
            return
        }
        
        const selectedUserIds = this._selectedUserIdsForRoleSelection(role)
        const previousUserIds = this._userIdsForRole(role)
        const addedUserIds = selectedUserIds.filter(userId => !previousUserIds.contains(userId))

        const currentUserId = this._currentUserId()
        if (
            this._canCurrentUserRequestTeamSelfAddition() &&
            addedUserIds.contains(currentUserId)
        ) {
            this._reloadUsersDataView()
            if (role !== "responsible") {
                this.currentUserSelfAdditionWasRequested?.(role)
            }
            return
        }
        
        if (addedUserIds.length > 0) {
            for (const addedUserId of addedUserIds) {
                this._removeUserFromRole(addedUserId, "responsible")
                this._removeUserFromRole(addedUserId, "authorized")
                this._removeUserFromRole(addedUserId, "observer")
                this._removeUserFromRole(addedUserId, "evaluator")
                this._addUserToRole(addedUserId, role)
            }
        }
        else {
            this._replaceUserIdsForRole(role, selectedUserIds)
        }
        
        this._deduplicateAcrossRoles()
        this._ensureCurrentUserHasRole()
        this._ensureKnownUsersForSelections()
        this._reloadUsersDataView()
        this.teamDidChange?.()
    }
    
    _selectedUserIdsForRoleSelection(role: ProcurementTeamEditableRole): string[] {
        const selectedUserIds: string[] = []
        const selectedItems = this.usersDataView.selectionForIdentifier(this._selectionIdentifierForRole(role))
        for (const selectedItem of selectedItems) {
            const userId = selectedItem.dataItem.userId
            if (userId.length > 0 && !selectedUserIds.contains(userId)) {
                selectedUserIds.push(userId)
            }
        }
        return selectedUserIds
    }
    
    _userIdsForRole(role: ProcurementTeamEditableRole): string[] {
        if (role === "responsible") {
            const responsibleUserIds: string[] = []
            if (this._responsibleUserId.length > 0) {
                responsibleUserIds.push(this._responsibleUserId)
            }
            return responsibleUserIds
        }
        if (role === "authorized") {
            return [...this._authorizedUserIds]
        }
        if (role === "observer") {
            return [...this._observerUserIds]
        }
        return [...this._evaluatorUserIds]
    }
    
    _replaceUserIdsForRole(role: ProcurementTeamEditableRole, userIds: string[]) {
        if (role === "responsible") {
            this._responsibleUserId = ""
            const firstUserId = userIds.firstElement
            if (firstUserId) {
                this._responsibleUserId = firstUserId
            }
            return
        }
        if (role === "authorized") {
            this._authorizedUserIds = this._uniqueUserIds(userIds)
            return
        }
        if (role === "observer") {
            this._observerUserIds = this._uniqueUserIds(userIds)
            return
        }
        this._evaluatorUserIds = this._uniqueUserIds(userIds)
    }
    
    _addUserToRole(userId: string, role: ProcurementTeamEditableRole) {
        if (role === "responsible") {
            this._responsibleUserId = userId
            return
        }
        if (role === "authorized") {
            if (!this._authorizedUserIds.contains(userId)) {
                this._authorizedUserIds.push(userId)
            }
            return
        }
        if (role === "observer") {
            if (!this._observerUserIds.contains(userId)) {
                this._observerUserIds.push(userId)
            }
            return
        }
        if (!this._evaluatorUserIds.contains(userId)) {
            this._evaluatorUserIds.push(userId)
        }
    }
    
    _removeUserFromRole(userId: string, role: ProcurementTeamEditableRole) {
        if (role === "responsible") {
            if (this._responsibleUserId === userId) {
                this._responsibleUserId = ""
            }
            return
        }
        if (role === "authorized") {
            this._authorizedUserIds = this._authorizedUserIds.filter(selectedUserId => selectedUserId !== userId)
            return
        }
        if (role === "observer") {
            this._observerUserIds = this._observerUserIds.filter(selectedUserId => selectedUserId !== userId)
            return
        }
        this._evaluatorUserIds = this._evaluatorUserIds.filter(selectedUserId => selectedUserId !== userId)
    }
    
    _reloadUsersDataView() {
        const scrollTop = this.usersDataView.tableView.viewHTMLElement.scrollTop
        const rows = this._rowsForUsersDataView()
        
        for (const rowData of this._rowDataByUserId.values()) {
            const role = this._roleForUserId(rowData.userId)
            rowData.responsibleSelected = role === "responsible"
            rowData.authorizedSelected = role === "authorized"
            rowData.observerSelected = role === "observer"
            rowData.evaluatorSelected = role === "evaluator"
            rowData.conflictOfInterestStatusText = this._conflictOfInterestStatusTextForUserId(rowData.userId)
            rowData.hasBidderSideParticipation = this._bidderSideParticipantUserIds.has(rowData.userId)
        }
        for (const role of ["responsible", "authorized", "observer", "evaluator"] as ProcurementTeamEditableRole[]) {
            const selectionTokens = new Set<string>()
            for (const userId of this._userIdsForRole(role)) {
                const rowData = this._rowDataByUserId.get(userId)
                if (rowData) {
                    selectionTokens.add(CBDataView._tokenForItem(rowData))
                }
            }
            CBDataView._selectionStore.set(this._selectionIdentifierForRole(role), selectionTokens)
        }
        
        let noDataMessage: string
        if (this._usersLoadState === "loading") {
            noDataMessage = "Kasutajaid laaditakse..."
        }
        else if (this._usersLoadState === "failed") {
            noDataMessage = "Kasutajaid ei saanud laadida."
        }
        else if (this._allUsers.length === 0) {
            noDataMessage = "Aktiivseid töötajaid ei leitud."
        }
        else if (this.usersDataView.searchTextField.text.trim().length > 0) {
            noDataMessage = "Töötajaid ei leitud."
        }
        else {
            noDataMessage = "Otsi töötajat nime või e-posti järgi."
        }
        
        this.usersDataView.noDataMessage = noDataMessage
        this.usersDataView.data = rows
        this.usersDataView.tableView.viewHTMLElement.scrollTop = scrollTop
        this._updateUsersContentVisibility()
        this.setNeedsLayoutUpToRootView()
    }

    _updateUsersContentVisibility() {
        this.errorBanner.removeFromSuperview()
        this.emptyStateBanner.removeFromSuperview()

        if (this._usersLoadState === "failed") {
            this.usersDataView.removeFromSuperview()
            this.errorBanner.addedAsSubviewToView(this)
            return
        }

        if (this._usersLoadState === "loaded" && this._allUsers.length === 0) {
            this.usersDataView.removeFromSuperview()
            this.emptyStateBanner.addedAsSubviewToView(this)
            return
        }

        if (this.usersDataView.superview !== this) {
            this.usersDataView.addedAsSubviewToView(this)
        }
    }
    
    _rowsForUsersDataView(): ProcurementTeamEmployeeRowData[] {
        if (this._usersLoadState !== "loaded") {
            return []
        }
        
        const rows: ProcurementTeamEmployeeRowData[] = []
        
        for (const member of this._allUsers) {
            rows.push(this._rowDataForMember(member))
        }
        
        return rows
    }
    
    _rowDataForMember(member: RHTeamMemberDisplayData): ProcurementTeamEmployeeRowData {
        const identityPresentation =
            RHCompanyMemberPresentation.identityPresentationForPerson(member)
        const name = identityPresentation.displayName ?? "-"
        const nameHTML = identityPresentation.escapedDisplayName ?? "-"
        const email = identityPresentation.email

        const employeeSearchText = [
            name,
            email,
            member.userId,
            this._companyNamePresentationByUserId.get(member.userId)?.text ?? ""
        ].join(" ")
        const companyNamePresentation = this._companyNamePresentationByUserId.get(member.userId) ?? {
            text: "",
            tooltip: ""
        }
        
        let rowData = this._rowDataByUserId.get(member.userId)
        if (!rowData) {
            rowData = {
                userId: member.userId,
                name: "",
                nameHTML: "",
                email: "",
                emailHTML: "",
                employeeSearchText: "",
                companyName: "",
                companyNameTooltip: "",
                responsibleSelected: NO,
                authorizedSelected: NO,
                observerSelected: NO,
                evaluatorSelected: NO,
                conflictOfInterestStatusText: "—",
                hasBidderSideParticipation: NO
            }
            this._rowDataByUserId.set(member.userId, rowData)
        }
        
        const role = this._roleForUserId(member.userId)
        rowData.name = name
        rowData.nameHTML = nameHTML
        rowData.email = email
        rowData.emailHTML = identityPresentation.escapedEmail
        rowData.employeeSearchText = employeeSearchText
        rowData.companyName = companyNamePresentation.text
        rowData.companyNameTooltip = companyNamePresentation.tooltip
        rowData.responsibleSelected = role === "responsible"
        rowData.authorizedSelected = role === "authorized"
        rowData.observerSelected = role === "observer"
        rowData.evaluatorSelected = role === "evaluator"
        rowData.conflictOfInterestStatusText = this._conflictOfInterestStatusTextForUserId(member.userId)
        rowData.hasBidderSideParticipation = this._bidderSideParticipantUserIds.has(member.userId)
        return rowData
    }

    _conflictOfInterestStatusTextForUserId(userId: string): string {
        const role = this._roleForUserId(userId)
        if (this._bidderSideParticipantUserIds.has(userId)) {
            if (!role) {
                return "Kasutaja osaleb sellel hankel pakkuja poolel. Hankemeeskonda lisamisel tekib võimalik huvide konflikt."
            }
            return BIDDER_TEAM_CONFLICT_MESSAGE
        }
        if (!role) {
            return "—"
        }
        const confirmation = this._conflictOfInterestConfirmations.find(candidate =>
            RHTeamUserReferenceResolver.userIdFromReference(candidate.user) === userId &&
            candidate.roleSnapshot === role
        )
        if (!confirmation) {
            return "Kinnitamata"
        }
        return formatRHDateTimeWithSecondsValue(confirmation.confirmedAt)
    }

    _reloadHistoryDataView() {
        this.historyTitleLabel.removeFromSuperview()
        this.historyExplanationLabel.removeFromSuperview()
        this.historyDataView.removeFromSuperview()
        if (this.showsHistory !== YES) {
            return
        }
        this.historyTitleLabel.addedAsSubviewToView(this)
        this.historyExplanationLabel.addedAsSubviewToView(this)
        this.historyDataView.addedAsSubviewToView(this)
        this.historyDataView.data = this._historyRows()
    }

    _historyRows(): ProcurementTeamHistoryRowData[] {
        const rows: ProcurementTeamHistoryRowData[] = []
        for (const entry of this._historyEntries) {
            const eventData = entry.eventData as RHTeamHistoryEventData
            const actor = this._displayNameForUserId(entry.actor.userId, entry.actor.userEmail)
            const timestamp = entry.timestamp
            if (
                entry.eventType === "procurement.team_updated" ||
                entry.eventType === "procurement.tenderer_team_updated" ||
                entry.eventType === "offer.team_updated"
            ) {
                let addedMembers: Array<{ userId: string, role: string, memberLabel?: string }> = []
                if (Array.isArray(eventData.addedMembers)) {
                    addedMembers = eventData.addedMembers as Array<{
                        userId: string
                        role: string
                        memberLabel?: string
                    }>
                }
                let removedMembers: Array<{ userId: string, role: string, memberLabel?: string }> = []
                if (Array.isArray(eventData.removedMembers)) {
                    removedMembers = eventData.removedMembers as Array<{
                        userId: string
                        role: string
                        memberLabel?: string
                    }>
                }
                for (const member of addedMembers) {
                    rows.push(this._historyRow(entry._id + "added" + member.userId + member.role,
                        timestamp, actor, "Lisati meeskonda", member.userId, member.role, member.memberLabel))
                }
                for (const member of removedMembers) {
                    rows.push(this._historyRow(entry._id + "removed" + member.userId + member.role,
                        timestamp, actor, "Eemaldati meeskonnast", member.userId, member.role, member.memberLabel))
                }
                if (addedMembers.length === 0 && removedMembers.length === 0) {
                    rows.push(this._historyRow(entry._id, timestamp, actor, "Meeskonda muudeti", "", ""))
                }
            }
            else if (
                entry.eventType === "procurement.responsible_changed" ||
                entry.eventType === "offer.responsible_changed"
            ) {
                const previousResponsibleUserId = this._stringFromHistoryValue(
                    eventData.previousResponsibleUserId
                )
                const responsibleUserId = this._stringFromHistoryValue(eventData.responsibleUserId)
                if (previousResponsibleUserId.length > 0 && previousResponsibleUserId !== responsibleUserId) {
                    rows.push(this._historyRow(entry._id + "previous", timestamp, actor,
                        "Eemaldati vastutava isiku rollist", previousResponsibleUserId, "responsible",
                        this._stringFromHistoryValue(eventData.previousResponsibleUserLabel)))
                }
                rows.push(this._historyRow(entry._id + "current", timestamp, actor,
                    "Määrati vastutavaks", responsibleUserId, "responsible",
                    this._stringFromHistoryValue(eventData.responsibleUserLabel)))
            }
            else if (
                entry.eventType === "procurement.self_added_to_team" ||
                entry.eventType === "offer.self_added_to_team"
            ) {
                rows.push(this._historyRow(entry._id, timestamp, actor, "Lisas end meeskonda",
                    this._stringFromHistoryValue(eventData.userId) || entry.actor.userId || "",
                    this._stringFromHistoryValue(eventData.role),
                    this._stringFromHistoryValue(eventData.userLabel)))
            }
            else if (entry.eventType === "procurement.conflict_confirmed") {
                rows.push(this._historyRow(entry._id, timestamp, actor,
                    "Kinnitas huvide konflikti puudumist",
                    this._stringFromHistoryValue(eventData.userId) || entry.actor.userId || "",
                    this._stringFromHistoryValue(eventData.roleSnapshot),
                    this._stringFromHistoryValue(eventData.userLabel)))
            }
        }
        return rows
    }

    _historyRow(
        identifier: string,
        timestamp: Date,
        actor: string,
        action: string,
        memberUserId: string,
        role: string,
        memberLabel?: string
    ): ProcurementTeamHistoryRowData {
        const member = this._displayNameForUserId(memberUserId, memberLabel)
        const roleLabel = this._roleLabel(role)
        return {
            identifier,
            timestamp: RHTimestampSorting.sortableISOString(timestamp),
            timestampText: formatRHDateTimeWithSecondsValue(timestamp),
            actor,
            action,
            member,
            role: roleLabel
        }
    }

    _displayNameForUserId(userId?: string, fallback?: string): string {
        if (userId) {
            const member = this._allUsers.find(candidate => candidate.userId === userId)
            if (member) {
                return member.name || member.email || userId
            }
        }
        return fallback || userId || "—"
    }

    _stringFromHistoryValue(value: string | undefined): string {
        if (typeof value === "string") {
            return value
        }
        return ""
    }

    _roleLabel(role: string): string {
        if (role === "responsible") { return "Vastutav" }
        if (role === "authorized") { return "Volitatud" }
        if (role === "observer") { return "Vaatleja" }
        if (role === "evaluator") { return "Hindaja" }
        return role
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        const padding = RHSpacing.medium
        // Span the full width so the section title, explanation and table line up with the
        // form's full-width input fields; keep the vertical inset for the section's spacing.
        const bounds = this.contentBoundsWithInsets(0, 0, padding, padding)
        const teamContentView = IF(this.errorBanner.superview === this)(() => this.errorBanner)
            .ELSE_IF(this.emptyStateBanner.superview === this)(() => this.emptyStateBanner)
            .ELSE(() => this.usersDataView)
        const hasTitleInLayout = this.titleLabel.superview === this
        const hasExplanationInLayout = this.explanationLabel.superview === this
        const hasCompanyMemberInvitationNoticeInLayout =
            this.companyMemberInvitationCalloutView.superview === this
        const hasCurrentUserSelfAdditionCalloutInLayout =
            this.currentUserSelfAdditionCalloutView.superview === this
        const hasHeaderContentInLayout =
            hasTitleInLayout ||
            hasExplanationInLayout ||
            hasCompanyMemberInvitationNoticeInLayout ||
            hasCurrentUserSelfAdditionCalloutInLayout
        const hasHistoryInLayout = this.historyDataView.superview === this

        bounds
            .rectangleWithHeight(0)
            .IF(hasTitleInLayout)
            .rectangleForNextRow(0, this.titleLabel)
            .performingFunctionWithSelf(rowFrame =>
                this.templateLockButton.layoutBesideTitleLabelInFrame(this.titleLabel, rowFrame)
            )
            .ENDIF()
            .IF(hasExplanationInLayout)
            .rectangleForNextRow(
                IF(hasTitleInLayout)(() => RHSpacing.small).ELSE(() => 0),
                this.explanationLabel
            )
            .assignedAsFrameOfView(this.explanationLabel)
            .ENDIF()
            .IF(hasCompanyMemberInvitationNoticeInLayout)
            .rectangleForNextRow(
                IF(hasTitleInLayout || hasExplanationInLayout)(() => RHSpacing.small).ELSE(() => 0),
                this.companyMemberInvitationCalloutView
            )
            .assignedAsFrameOfView(this.companyMemberInvitationCalloutView)
            .ENDIF()
            .IF(hasCurrentUserSelfAdditionCalloutInLayout)
            .rectangleForNextRow(
                IF(
                    hasTitleInLayout ||
                    hasExplanationInLayout ||
                    hasCompanyMemberInvitationNoticeInLayout
                )(() => RHSpacing.small).ELSE(() => 0),
                this.currentUserSelfAdditionCalloutView
            )
            .assignedAsFrameOfView(this.currentUserSelfAdditionCalloutView)
            .ENDIF()
            .rectangleForNextRow(
                IF(hasHeaderContentInLayout)(() => RHSpacing.small).ELSE(() => 0),
                teamContentView
            )
            .assignedAsFrameOfView(teamContentView)
            .IF(hasHistoryInLayout)
            .rectangleForNextRow(RHSpacing.large, this.historyTitleLabel)
            .assignedAsFrameOfView(this.historyTitleLabel)
            .rectangleForNextRow(RHSpacing.small, this.historyExplanationLabel)
            .assignedAsFrameOfView(this.historyExplanationLabel)
            .rectangleForNextRow(RHSpacing.small, this.historyDataView)
            .assignedAsFrameOfView(this.historyDataView)
            .ENDIF()
    }
    
}


export { ProcurementTeamView as RHTeamRoleMatrixView }
